/**
 * Notstrom anklemmen: Ader anklicken, dann die passende Klemme.
 *
 * Fehlgriffe prüft die Oberfläche selbst (das Rätsel liegt ja offen) und
 * zieht dafür Zeit ab; der Server prüft am Ende die vollständige Belegung.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { CABLING, WIRE_COLORS, type CablingPuzzle, type Wire } from '@hosting-sim/core';
import type { MinigameApi } from './MinigameOverlay';

const ROW = 56;
const TOP = 40;
const LEFT_X = 150;
const RIGHT_X = 520;

function WireStroke({ wire, x1, y1, x2, y2, width = 10 }: { wire: Wire; x1: number; y1: number; x2: number; y2: number; width?: number }) {
  const base = WIRE_COLORS[wire.base]?.hex ?? '#888';
  const stripe = wire.stripe ? WIRE_COLORS[wire.stripe]?.hex : null;
  const mid = (x1 + x2) / 2;
  const d = `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`;
  return (
    <g>
      <path d={d} stroke="#0a0a0a" strokeWidth={width + 3} fill="none" strokeLinecap="round" />
      <path d={d} stroke={base} strokeWidth={width} fill="none" strokeLinecap="round" />
      {stripe && <path d={d} stroke={stripe} strokeWidth={width * 0.45} strokeDasharray="7 7" fill="none" />}
    </g>
  );
}

export function CablingGame({ puzzle, api, disabled }: { puzzle: CablingPuzzle; api: MinigameApi; disabled: boolean }) {
  const [selected, setSelected] = useState<number | null>(null);
  /** Ader → Klemme. */
  const [mapping, setMapping] = useState<Record<number, number>>({});
  const [order, setOrder] = useState<number[]>([]);
  const [spark, setSpark] = useState<{ terminal: number; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const n = puzzle.wires.length;
  const height = TOP * 2 + ROW * (n - 1);
  const earth = puzzle.wires.findIndex((w) => w.earth);
  const usedCodes = [...new Set(puzzle.wires.flatMap((w) => [w.base, ...(w.stripe ? [w.stripe] : [])]))];

  async function connect(terminal: number) {
    if (disabled || busy || selected === null) return;
    if (Object.values(mapping).includes(terminal)) return;
    const wire = selected;

    if (order.length === 0 && wire !== earth) {
      setSpark({ terminal, text: 'Erst den Schutzleiter (GN/GE)!' });
      api.penalize(CABLING.PENALTY_SECONDS * 1000);
      setSelected(null);
      return;
    }
    if (puzzle.terminals[terminal] !== wire) {
      setSpark({ terminal, text: 'Funkenschlag! Falsche Klemme.' });
      api.penalize(CABLING.PENALTY_SECONDS * 1000);
      setSelected(null);
      return;
    }

    setSpark(null);
    const nextMapping = { ...mapping, [wire]: terminal };
    const nextOrder = [...order, wire];
    setMapping(nextMapping);
    setOrder(nextOrder);
    setSelected(null);

    if (nextOrder.length === n) {
      setBusy(true);
      const result = await api.submit({ mapping: puzzle.wires.map((_, i) => nextMapping[i]), order: nextOrder });
      setBusy(false);
      api.finish(result.ok, result.message);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      <div>
        <p className="mb-2 text-sm text-zinc-300">
          Der Netzbetreiber ist weg, die mobile Netzersatzanlage steht vor der Tür. Klemme jede Ader auf die Klemme mit ihrem
          Farbkürzel. <strong className="text-amber-300">Zuerst den Schutzleiter</strong>, sonst steht das Gehäuse unter Spannung.
        </p>
        <svg viewBox={`0 0 680 ${height}`} className="w-full select-none rounded-lg bg-zinc-800" role="img" aria-label="Schaltschrank">
          {/* Schrank rechts */}
          <rect x={RIGHT_X - 20} y={8} width={150} height={height - 16} rx={8} fill="#3f3f46" stroke="#71717a" />
          <text x={RIGHT_X + 55} y={26} textAnchor="middle" fontSize={11} fill="#d4d4d8">
            Notstrom-Einspeisung
          </text>
          {/* Aggregat links */}
          <rect x={10} y={8} width={70} height={height - 16} rx={8} fill="#365314" stroke="#65a30d" />
          <text x={45} y={height / 2} textAnchor="middle" fontSize={11} fill="#ecfccb" transform={`rotate(-90 45 ${height / 2})`}>
            Netzersatzanlage
          </text>

          {/* Verbundene Adern */}
          {Object.entries(mapping).map(([wire, terminal]) => (
            <WireStroke
              key={`c${wire}`}
              wire={puzzle.wires[Number(wire)]}
              x1={LEFT_X}
              y1={TOP + Number(wire) * ROW}
              x2={RIGHT_X}
              y2={TOP + terminal * ROW}
            />
          ))}

          {/* Adern links */}
          {puzzle.wires.map((wire, i) => {
            const y = TOP + i * ROW;
            const done = mapping[i] !== undefined;
            return (
              <g
                key={`w${i}`}
                onClick={() => !done && !disabled && setSelected(i === selected ? null : i)}
                className={clsx(!done && !disabled && 'cursor-pointer')}
              >
                <WireStroke wire={wire} x1={80} y1={y} x2={LEFT_X} y2={y} width={12} />
                <circle
                  cx={LEFT_X}
                  cy={y}
                  r={selected === i ? 13 : 10}
                  fill={done ? '#52525b' : '#e4e4e7'}
                  stroke={selected === i ? '#facc15' : '#18181b'}
                  strokeWidth={selected === i ? 4 : 2}
                />
                {selected === i && (
                  <circle cx={LEFT_X} cy={y} r={18} fill="none" stroke="#facc15" strokeWidth={2} className="animate-ping" style={{ transformOrigin: `${LEFT_X}px ${y}px` }} />
                )}
              </g>
            );
          })}

          {/* Klemmen rechts */}
          {puzzle.terminals.map((_, t) => {
            const y = TOP + t * ROW;
            const used = Object.values(mapping).includes(t);
            const code = puzzle.wires[puzzle.terminals[t]].code;
            return (
              <g key={`t${t}`} onClick={() => void connect(t)} className={clsx(!used && selected !== null && 'cursor-pointer')}>
                <rect
                  x={RIGHT_X - 10}
                  y={y - 14}
                  width={28}
                  height={28}
                  rx={4}
                  fill={used ? '#16a34a' : selected !== null ? '#a1a1aa' : '#71717a'}
                  stroke="#18181b"
                  strokeWidth={2}
                />
                <circle cx={RIGHT_X + 4} cy={y} r={5} fill="#27272a" />
                <rect x={RIGHT_X + 26} y={y - 11} width={78} height={22} rx={3} fill="#fafaf9" />
                <text x={RIGHT_X + 65} y={y + 5} textAnchor="middle" fontSize={13} fontFamily="monospace" fontWeight={700} fill="#18181b">
                  {code}
                </text>
                {spark?.terminal === t && (
                  <g>
                    <text x={RIGHT_X - 20} y={y - 18} textAnchor="end" fontSize={12} fill="#fde047" fontWeight={700}>
                      ⚡ {spark.text}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
        <p className="mt-2 text-xs text-zinc-400">
          {selected === null ? 'Ader links anklicken …' : `… und jetzt die Klemme für diese Ader.`} Fehlgriff kostet{' '}
          {CABLING.PENALTY_SECONDS} Sekunden. {order.length}/{n} angeklemmt.
        </p>
      </div>

      {/* Aufkleber in der Schranktür */}
      <aside className="h-fit rounded-lg border-2 border-dashed border-amber-400/60 bg-amber-50 p-3 text-zinc-900">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide">Farbkürzel nach DIN</p>
        <ul className="space-y-1 text-sm">
          {usedCodes.concat(['GN', 'GE']).filter((c, i, a) => a.indexOf(c) === i).map((code) => (
            <li key={code} className="flex items-center gap-2">
              <span className="inline-block h-3 w-6 rounded-sm border border-zinc-500" style={{ background: WIRE_COLORS[code].hex }} />
              <span className="w-8 font-mono font-bold">{code}</span>
              <span>{WIRE_COLORS[code].label}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-zinc-600">Zweifarbig: Grundfarbe/Streifen, z. B. RT/WS = rot mit weißem Streifen.</p>
      </aside>
    </div>
  );
}
